// src/services/movementAnalysisService.js
import { loadLineChunkRowsForDateRange } from "../features/salesDataImport/services/salesLineChunksReader";
import {
  rowMatchesDateRange,
  rowTranDateYmd,
} from "../features/salesDataImport/utils/dateParse";
import { readStoredFinancialYearStartYear } from "../utils/financialYear";
import {
  importRowBrandNameField,
  invoiceTypeFromImportRow,
  isCreditSalesBrRow,
  isCreditSalesRow,
  isCreditSalesSparewayRow,
} from "./salesImportTransactionDataService";

const movementCache = new Map();
const MOVEMENT_CACHE_MAX = 12;

function cacheMovement(key, data) {
  if (movementCache.size >= MOVEMENT_CACHE_MAX) {
    const first = movementCache.keys().next().value;
    movementCache.delete(first);
  }
  movementCache.set(key, data);
}

export function clearMovementAnalysisCache() {
  movementCache.clear();
}

function isCreditSaleRow(row) {
  return isCreditSalesRow(row) || isCreditSalesSparewayRow(row) || isCreditSalesBrRow(row);
}

function monthKeyFromYmd(ymd) {
  if (!ymd || ymd.length < 7) return "";
  return ymd.slice(0, 7);
}

function partyFromRow(row) {
  return String(
    row?.customerName ?? row?.customer ?? row?.party ?? row?.partyName ?? ""
  ).trim() || "-";
}

function rateFromRow(row) {
  return Number(row?.unitPrice ?? row?.rate ?? row?.stdCost ?? 0) || 0;
}

function createPartEntry(row, code) {
  const brand = importRowBrandNameField(row) || String(row?.itemGroup ?? row?.itemgroup ?? "").trim();
  return {
    code,
    name: String(row?.itemName ?? row?.desc ?? "-").trim() || "-",
    group: String(row?.itemGroup ?? row?.itemgroup ?? row?.group ?? "-").trim() || "-",
    brand: brand || "-",
    stdCost: rateFromRow(row),
    openingQty: 0,
    inwardQty: 0,
    outwardQty: 0,
    creditSalesQty: 0,
    closingQty: 0,
    closingValue: 0,
  };
}

function applyQty(entry, type, qty, isCredit) {
  if (type === "inward" || type === "creditnote") {
    entry.inwardQty += qty;
  } else if (type === "outward") {
    entry.outwardQty += qty;
    if (isCredit) entry.creditSalesQty += qty;
  }
}

function applyOpening(entry, type, qty) {
  if (type === "inward" || type === "creditnote") {
    entry.openingQty += qty;
  } else if (type === "outward") {
    entry.openingQty -= qty;
  }
}

function fyStartYmd() {
  const fy = readStoredFinancialYearStartYear();
  return `${fy}-04-01`;
}

function buildFromRows(rows, fromStr, toStr) {
  const parts = Object.create(null);
  const monthlyByPart = Object.create(null);
  const ledgerByKey = Object.create(null);

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const type = invoiceTypeFromImportRow(row);
    if (type === "transfer") continue;

    const code = String(row?.partNo ?? row?.code ?? "").trim();
    if (!code) continue;

    const qty = Number(row?.qty) || 0;
    if (!qty) continue;

    const ymd = rowTranDateYmd(row);
    const inPeriod = rowMatchesDateRange(row, fromStr, toStr);
    const beforePeriod = fromStr && ymd && ymd < fromStr;

    if (!inPeriod && !beforePeriod) continue;

    let entry = parts[code];
    if (!entry) {
      entry = createPartEntry(row, code);
      parts[code] = entry;
    } else {
      const rate = rateFromRow(row);
      if (rate > 0) entry.stdCost = rate;
    }

    const isCredit = type === "outward" && isCreditSaleRow(row);

    if (beforePeriod) {
      applyOpening(entry, type, qty);
      continue;
    }

    applyQty(entry, type, qty, isCredit);

    const monthKey = monthKeyFromYmd(ymd);
    if (monthKey) {
      if (!monthlyByPart[code]) monthlyByPart[code] = Object.create(null);
      let month = monthlyByPart[code][monthKey];
      if (!month) {
        month = { key: monthKey, inward: 0, outward: 0, value: 0 };
        monthlyByPart[code][monthKey] = month;
      }
      if (type === "inward" || type === "creditnote") {
        month.inward += qty;
      } else if (type === "outward") {
        month.outward += qty;
      }
      month.value += qty * (entry.stdCost || rateFromRow(row));

      const ledgerKey = `${code}|${monthKey}`;
      if (!ledgerByKey[ledgerKey]) ledgerByKey[ledgerKey] = [];
      const voucherNo = String(row?.voucherNo ?? row?.billNo ?? "").trim() || "-";
      const party = partyFromRow(row);
      const movementLabel =
        type === "inward" || type === "creditnote"
          ? type === "creditnote"
            ? "creditnote"
            : "inward"
          : "outward";
      const rowKey = `${voucherNo}|${movementLabel}|${ymd}|${party}`;
      const list = ledgerByKey[ledgerKey];
      let ledgerRow = list.find((r) => r._key === rowKey);
      if (!ledgerRow) {
        ledgerRow = {
          _key: rowKey,
          voucherNo,
          dateYmd: ymd,
          party,
          type: movementLabel,
          qty: 0,
          value: 0,
        };
        list.push(ledgerRow);
      }
      ledgerRow.qty += qty;
      ledgerRow.value += qty * (entry.stdCost || rateFromRow(row));
    }
  }

  const report = Object.values(parts).map((entry) => {
    const closingQty = entry.openingQty + entry.inwardQty - entry.outwardQty;
    const stdCost = Number(entry.stdCost) || 0;
    return {
      ...entry,
      closingQty,
      closingValue: Number((closingQty * stdCost).toFixed(2)),
    };
  });

  report.sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  for (const key of Object.keys(ledgerByKey)) {
    ledgerByKey[key].sort((a, b) => {
      const d = (a.dateYmd || "").localeCompare(b.dateYmd || "");
      if (d !== 0) return d;
      return (a.voucherNo || "").localeCompare(b.voucherNo || "");
    });
    ledgerByKey[key] = ledgerByKey[key].map(({ _key, ...rest }) => rest);
  }

  return { report, monthlyByPart, ledgerByKey, rowCount: rows.length };
}

/**
 * Movement analysis from uploaded sales import.
 * Opening qty = net movement from FY start through day before fromDate.
 * @param {string|null} fromDate YYYY-MM-DD
 * @param {string|null} toDate YYYY-MM-DD
 */
export async function getMovementReport(fromDate, toDate) {
  const fromStr = fromDate ? String(fromDate).slice(0, 10) : "";
  const toStr = toDate ? String(toDate).slice(0, 10) : "";
  const cacheKey = `${fromStr}|${toStr}`;

  if (movementCache.has(cacheKey)) {
    return movementCache.get(cacheKey);
  }

  const loadFrom = fromStr ? (fromStr > fyStartYmd() ? fyStartYmd() : fromStr) : fromStr;
  const { rows } = await loadLineChunkRowsForDateRange({
    fromDate: loadFrom || undefined,
    toDate: toStr || undefined,
  });

  const result = buildFromRows(rows, fromStr, toStr);
  cacheMovement(cacheKey, result);
  return result;
}
